import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { BackInfoActions } from '../../Reducer/BackInfoReducer';
import { runBackHandler } from './backHandlerRegistry';
import { useAppNavigate } from './useAppNavigate';

/**
 * 앱 전체 뒤로가기 처리 훅
 * 최상위 컴포넌트(Layout.jsx)에서 한 번만 호출할 것
 *
 * 처리 우선순위:
 * 1. stack top이 가로채기 타입 (sheet/dialog/drawer/snackbar/custom)
 *    → onBack 핸들러 실행
 * 2. stack top이 page 타입
 *    → 저장된 경로로 navigate + pop
 * 3. 홈 화면 → 앱 종료 확인 (Android)
 * 4. 그 외 → popPage()
 */
export function useBackHandler() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { popPage } = useAppNavigate();

  const { stack, isProcessing } = useSelector((state) => state.backInfo);

  const handleBack = useCallback(async () => {
    if (isProcessing) return;
    dispatch(BackInfoActions.setProcessing(true));
    console.group('--- [BackStack Status] ---');
    console.log('현재 스택 개수:', stack.length);
    console.log('전체 스택 리스트:', stack);

    try {
      if (stack.length > 0) {
        const top = stack[stack.length - 1];
        console.log(`[BackStack] top 처리: ${top.type} (id: ${top.id})`);

        // 가로채기 타입 → 핸들러 실행
        // (pop은 onBack 내부에서 상태 변경 → 컴포넌트 언마운트 → 자동 removeById)
        if (top.type !== 'page') {
          runBackHandler(top.handlerKey);
          return;
        }

        // page 타입 → 저장된 경로로 이동 + pop
        popPage();
        return;
      }

      // 홈 화면 → 앱 종료 확인 (Android)
      if (location.pathname === '/Home' || location.pathname === '/') {
        if (
          Capacitor.isNativePlatform() &&
          Capacitor.getPlatform() === 'android'
        ) {
          if (window.confirm('앱을 종료하시겠습니까?')) {
            CapacitorApp.exitApp();
          }
        }
        return;
      }

      // 스택형 뒤로가기로 변경: 일반 페이지 복귀도 BackStack 기준으로 처리
      popPage();
    } finally {
      dispatch(BackInfoActions.setProcessing(false));
    }
  }, [stack, isProcessing, location.pathname, popPage, dispatch]);

  // handleBack이 바뀔 때마다 ref 업데이트
  const handleBackRef = useRef(handleBack);
  useEffect(() => {
    handleBackRef.current = handleBack;
  }, [handleBack]);

  // 리스너는 최초 1회만 등록
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let listener;
    const setup = async () => {
      listener = await CapacitorApp.addListener('backButton', () => {
        handleBackRef.current();
      });
    };
    setup();
    return () => listener?.remove();
  }, []);

  return handleBack;
}
